/**
 * AI 自動グルーピングダイアログ：キーワード入力 → ドライラン → 名前入力 → 保存。
 * 仕様書: docs/01_spec/spec_mail_system.md B-11.3
 */
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { functions } from '@/lib/firebase';

interface SamplePreview {
  id: string;
  name: string;
  defaultEmail: string;
  tags: string[];
}

interface AiGroupResponse {
  ok: boolean;
  matchedContactIds: string[];
  samplePreview: SamplePreview[];
  reasoning?: string;
  groupId?: string;
}

const aiGroupContactsFn = httpsCallable<
  {
    keywords: string[];
    dryRun: boolean;
    name?: string;
    description?: string;
    /** ドライラン結果を再送して Claude 呼び出しを省略 */
    matchedContactIds?: string[];
  },
  AiGroupResponse
>(functions, 'aiGroupContacts', {
  // v3.3.x: 5000 件 + Claude 推論で 70 秒のデフォルトを超えやすいので 9 分に延長
  timeout: 540_000,
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AiGroupingDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [dryResult, setDryResult] = useState<AiGroupResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setKeywords([]);
      setKeywordInput('');
      setDryResult(null);
      setName('');
      setDescription('');
    }
  }, [open]);

  const addKeyword = (raw: string) => {
    const parts = raw
      .split(/[,、\t\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    setKeywords((prev) => {
      const next = [...prev];
      for (const p of parts) {
        if (!next.includes(p)) next.push(p);
      }
      return next;
    });
    setKeywordInput('');
    setDryResult(null); // キーワード変更でドライラン結果を破棄
  };

  const removeKeyword = (k: string) => {
    setKeywords((prev) => prev.filter((x) => x !== k));
    setDryResult(null);
  };

  const runDryRun = async () => {
    const trailing = keywordInput.trim();
    const finalKeywords =
      trailing && !keywords.includes(trailing) ? [...keywords, trailing] : keywords;
    if (finalKeywords.length === 0) {
      toast.error('キーワードを 1 件以上入力してください');
      return;
    }
    setKeywords(finalKeywords);
    setKeywordInput('');
    setRunning(true);
    setDryResult(null);
    try {
      const res = await aiGroupContactsFn({
        keywords: finalKeywords,
        dryRun: true,
      });
      setDryResult(res.data);
      if (res.data.matchedContactIds.length === 0) {
        toast.error('該当する配信先が見つかりませんでした');
      }
    } catch (err) {
      toast.error(`AI 判定失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  const handleSave = async () => {
    if (!dryResult || dryResult.matchedContactIds.length === 0) return;
    if (!name.trim()) {
      toast.error('グループ名を入力してください');
      return;
    }
    setSaving(true);
    try {
      const res = await aiGroupContactsFn({
        keywords,
        dryRun: false,
        name: name.trim(),
        description: description.trim() || undefined,
        // ドライランの ID 配列を再送 → Claude 再呼び出し回避（レート制限消費しない）
        matchedContactIds: dryResult.matchedContactIds,
      });
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
      toast.success(
        `AI グループ「${name.trim()}」を作成しました（${res.data.matchedContactIds.length} 名）`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const matchedCount = dryResult?.matchedContactIds.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>AI で自動グルーピング</DialogTitle>
          <DialogDescription>
            キーワードを与えると Claude が氏名 / タグ / 会社名 / 職業 / 性別 / 都道府県 / カスタムフィールドを見て該当配信先を抽出します。
            メアド・電話番号はプライバシー保護のため AI には渡しません。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">
              キーワード（必須・複数可。Enter / カンマで確定）
            </Label>
            <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
              {keywords.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                >
                  {k}
                  <button
                    type="button"
                    onClick={() => removeKeyword(k)}
                    className="hover:text-[var(--accent-red)] -mr-0.5"
                    aria-label={`${k} を削除`}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder={
                  keywords.length === 0 ? '例: 経営者, NES Health 利用者' : '追加キーワード'
                }
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  const composing = e.nativeEvent.isComposing || e.keyCode === 229;
                  if (e.key === 'Enter' && !composing) {
                    e.preventDefault();
                    addKeyword(keywordInput);
                  } else if (
                    e.key === 'Backspace' &&
                    keywordInput === '' &&
                    keywords.length > 0 &&
                    !composing
                  ) {
                    removeKeyword(keywords[keywords.length - 1]);
                  }
                }}
                onBlur={() => {
                  if (keywordInput.trim()) addKeyword(keywordInput);
                }}
                className="flex-1 min-w-[12ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              />
            </div>
            {keywordInput.trim() && (
              <button
                type="button"
                onClick={() => addKeyword(keywordInput)}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[var(--accent)] hover:underline"
              >
                + 「{keywordInput.trim()}」をキーワードとして追加
              </button>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={runDryRun}
              disabled={running || (keywords.length === 0 && !keywordInput.trim())}
            >
              {running ? <Spinner className="size-3 mr-1" /> : <Sparkles className="size-4 mr-1" />}
              ドライラン実行（保存はされません）
            </Button>
          </div>

          {dryResult && (
            <div className="space-y-3 rounded border border-[var(--border)] p-3 bg-[var(--bg-elevated)]">
              <div className="flex items-baseline gap-3">
                <div>
                  <div className="text-2xl font-bold text-[var(--accent)]">
                    {matchedCount}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)]">該当配信先</div>
                </div>
                {dryResult.reasoning && (
                  <div className="text-xs text-[var(--text-secondary)] leading-relaxed flex-1">
                    {dryResult.reasoning}
                  </div>
                )}
              </div>

              {dryResult.samplePreview.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                    サンプル（先頭 {dryResult.samplePreview.length} 件）
                  </div>
                  <div className="space-y-1">
                    {dryResult.samplePreview.map((p) => (
                      <div key={p.id} className="text-xs flex items-center gap-2">
                        <span className="font-medium">{p.name || '（名前なし）'}</span>
                        <span className="text-[var(--text-muted)] font-mono">{p.defaultEmail}</span>
                        {p.tags.length > 0 && (
                          <span className="ml-auto flex gap-1">
                            {p.tags.slice(0, 3).map((t) => (
                              <span
                                key={t}
                                className="text-[10px] bg-[var(--bg-hover)] px-1.5 py-0.5 rounded-full text-[var(--text-muted)]"
                              >
                                {t}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {matchedCount > 0 && (
                <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                  <div>
                    <Label className="text-xs">グループ名（必須）</Label>
                    <Input
                      type="text"
                      placeholder="例: NES Health 関連の見込み客"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">説明（任意）</Label>
                    <Textarea
                      placeholder="このグループの目的・対象者など"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={!dryResult || matchedCount === 0 || !name.trim() || saving}
          >
            {saving ? <Spinner className="size-3 mr-1" /> : <Sparkles className="size-3.5 mr-1" />}
            グループとして保存{matchedCount > 0 ? `（${matchedCount} 名）` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
